/**
 * Tracks how many browser TABS are sharing one signed-in session, so closing a
 * browser releases the concurrent-user licence immediately instead of waiting
 * out the idle timeout.
 *
 * ## Why this is needed
 *
 * Every tab of one sign-in shares ONE JWT, therefore one `sid`, therefore one
 * `AI_UserSession` row. A naive `pagehide` → `/session/end` would revoke the
 * session that the user's OTHER tabs are still using and sign them out mid-work
 * — which is why the beacon was removed earlier. But without it, a browser that
 * is simply CLOSED keeps its licence for the full idle timeout (5 minutes by
 * default), so the next person is locked out for no reason. That was the
 * reported problem.
 *
 * The fix is to know whether the closing tab is the LAST one. Each tab claims a
 * slot in a `localStorage` registry (profile-wide, so all tabs of one browser
 * see it) and renews its timestamp on every heartbeat. On `pagehide` a tab drops
 * its own slot and, if no OTHER live slot remains, releases the session.
 *
 * ## Why timestamps, not just a count
 *
 * A tab that crashes, or is killed by the OS, never runs `pagehide` and would
 * leave a slot behind forever — the count would never reach zero and the licence
 * would never be released early. Slots therefore carry a `seenAt` that the
 * heartbeat renews, and any slot older than {@link STALE_AFTER_MS} is treated as
 * dead. Worst case (every tab dies at once) the server-side idle sweep still
 * reclaims the session, exactly as before — this layer only ever makes release
 * FASTER, never slower.
 *
 * Storage note: `localStorage` is shared across tabs of one browser profile but
 * NOT across browsers (Chrome and Edge each keep their own), which is precisely
 * the scope wanted — "has this browser any tab left open?".
 */

const KEY_PREFIX = 'alpaca.auth.tabs.';

/** This tab's own id lives in sessionStorage, which is per-TAB — so every tab
 *  gets a distinct id and a duplicated tab does not inherit its parent's. */
const TAB_ID_KEY = 'alpaca.auth.tabid';

/** A slot not renewed within this window is assumed dead (crashed tab, killed
 *  process). Derived from the heartbeat interval by {@link staleWindowFor} so it
 *  always spans a few missed beats rather than a hard-coded guess. */
export const STALE_AFTER_MS = 150_000;   // default: 2.5 × the 60 s heartbeat

interface TabSlot { id: string; seenAt: number }

/** Three missed heartbeats, floored at 45 s — long enough that a slow tab is
 *  never mistaken for a dead one, short enough to stay well under the server's
 *  idle timeout so this path still wins the race. */
export function staleWindowFor(heartbeatSeconds: number): number {
  return Math.max(45_000, heartbeatSeconds * 3 * 1000);
}

function tabId(): string {
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch {
    // Storage disabled (private mode / policy): fall back to a per-load id. The
    // registry then never reaches zero, so release falls back to the server's
    // idle sweep — degraded, never wrong.
    return `nostore-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function read(sid: string): TabSlot[] {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + sid);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as TabSlot[]).filter(s => s && typeof s.id === 'string') : [];
  } catch { return []; }
}

function write(sid: string, slots: TabSlot[]): void {
  try {
    if (slots.length === 0) localStorage.removeItem(KEY_PREFIX + sid);
    else localStorage.setItem(KEY_PREFIX + sid, JSON.stringify(slots));
  } catch { /* storage unavailable — degrade to the server idle sweep */ }
}

/** Claim / renew this tab's slot. Call on mount and on every heartbeat. */
export function touchTab(sid: string, staleMs = STALE_AFTER_MS): void {
  const me = tabId();
  const now = Date.now();
  const slots = read(sid).filter(s => s.id !== me && now - s.seenAt < staleMs);
  slots.push({ id: me, seenAt: now });
  write(sid, slots);
}

/**
 * Drop this tab's slot and report whether the browser has any OTHER live tab
 * left on this session.
 *
 * @returns `true` when this was the last one — the caller should release the
 *          session. `false` when other tabs are still open, in which case
 *          releasing would sign THEM out.
 */
export function releaseTab(sid: string, staleMs = STALE_AFTER_MS): boolean {
  const me = tabId();
  const now = Date.now();
  const others = read(sid).filter(s => s.id !== me && now - s.seenAt < staleMs);
  write(sid, others);
  return others.length === 0;
}

/** Forget the whole registry for a session — used on explicit sign-out, where
 *  the server has already revoked the row and stale slots would otherwise
 *  linger into the next sign-in. */
export function clearTabs(sid: string): void {
  write(sid, []);
}

/**
 * The whole `pagehide` decision in one testable place: should this page-hide
 * release the session?
 *
 * <p>Extracted from the effect so the two ways it can be WRONG are provable
 * rather than merely intended:</p>
 *
 *  - Releasing on a **bfcache freeze** (`persisted === true`). The page is not
 *    closing — it is being frozen for back/forward and may be restored — so the
 *    user would come back to a dead session and be thrown to the login screen
 *    just for pressing Back.
 *  - Releasing while **other tabs are open**, which signs them out mid-work.
 *
 * Consumes the tab slot as a side effect when it decides, which is deliberate:
 * the slot must be given up on a real close whether or not this tab turns out
 * to be the last one.
 */
export function shouldReleaseOnPageHide(
  opts: { persisted: boolean; sid: string | null; hasToken: boolean },
  staleMs = STALE_AFTER_MS,
): boolean {
  // Frozen, not closed — touch nothing at all. Dropping the slot here would
  // make a later close by a sibling tab look like the last one.
  if (opts.persisted) return false;
  // No sid: concurrent-login is off, or the token predates the feature. Nothing
  // to release. No token: already signed out, and /session/end is [Authorize].
  if (!opts.sid || !opts.hasToken) return false;
  return releaseTab(opts.sid, staleMs);
}

/** The `sid` claim of a JWT, or null when the token carries none (feature off,
 *  or a token minted before concurrent-login shipped). Decodes WITHOUT verifying
 *  — this is only used to key local bookkeeping, never to make a trust decision;
 *  the server validates the token on every request. */
export function sidFromToken(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const sid = (JSON.parse(json) as { sid?: unknown }).sid;
    return typeof sid === 'string' && sid.length > 0 ? sid : null;
  } catch { return null; }
}
